import {
  CardMedia,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  TextField,
  Theme,
} from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { LocalizationProvider, DesktopDatePicker } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { Box, SxProps } from '@mui/system';
import { useField } from 'formik';
import { useState, useRef, useEffect } from 'react';
import {
  checkAsterikInString,
  isDateSame,
  RemoveAsterikFromstring,
} from '../utils/utilityFunctions';
import momentTimezone from 'moment-timezone';
import AdapterMoment from '@mui/lab/AdapterMoment';
import { timeZoneFromServer } from '../utils/constant';

const defaultTextFieldStyles: SxProps<Theme> = {
  'label + &': {
    mt: 4,
  },
  '& .MuiInputBase-root': {
    border: '1px solid #DDEAF3',
  },
  '& .MuiOutlinedInput-root': {
    position: 'relative',
    backgroundColor: 'common.white',
    border: '1px solid #DDEAF3',
    fontSize: 16,
    '&:hover': {
      borderColor: 'primary.main',
      '.MuiOutlinedInput-notchedOutline': {
        border: 0,
      },
    },
    '.MuiOutlinedInput-notchedOutline': {
      border: 0,
    },
    '.MuiInputBase-input': {
      p: '10px 12px',
    },
    '&:focus-visible': {
      outline: 'none',
    },
  },
};

export const TextDatePicker = ({
  label = '',
  placeholder = '',
  name,
  inputLabelStyles,
  textFieldStyles = defaultTextFieldStyles,
  maxDate = new Date(),
  minDate,
  disableHighlightToday = false,
  disabled = false,
  onClick,
  availableDates = [],
  value,
}: {
  label?: string;
  placeholder: string;
  value?: string | null;
  onClick?: (val?: any) => void;
  name: string;
  inputLabelStyles?: SxProps<Theme>;
  textFieldStyles?: SxProps<Theme>;
  maxDate?: Date;
  minDate?: Date;
  disableHighlightToday?: boolean;
  disabled?: boolean;
  availableDates?: string[];
}): JSX.Element => {
  const [field, meta, { setValue, setTouched }] = useField(name);
  const [openPicker, setOpenPicker] = useState(false);
  const [currentView, setCurrentView] = useState<'year' | 'month' | 'day'>('day');
  const iconClicked = useRef(false);
  const { moment } = new AdapterMoment({ instance: momentTimezone });
  moment.tz.setDefault(timeZoneFromServer);
  momentTimezone.tz.setDefault(timeZoneFromServer);

  const onChange = (date: any) => {
    const isValidDate = !!date && moment(date).isValid();
    const date1 = moment(new Date());
    const date2 = isValidDate
      ? moment(date).get('hours') === 0 &&
        moment(date).get('minutes') === 0 &&
        moment(date).get('seconds') === 0
        ? moment(date)
            .add(date1.get('hours'), 'hours')
            .add(date1.get('minutes'), 'minutes')
            .add(date1.get('seconds'), 'seconds')
            .format()
        : moment(date).format()
      : null;
    setValue(date2);
    setTouched(true, false);
    onClick && onClick(date2 ? moment(date2).format('YYYY-MM-DD') : null);
    if (currentView === 'day') setOpenPicker(false);
  };
  const handleViewChange = (view: 'year' | 'month' | 'day') => {
    setCurrentView(view);
  };
  const errorText = meta.error && meta.touched ? meta.error : '';
  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      iconClicked.current = true;
      setOpenPicker(!openPicker);
    }
  };
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <ClickAwayListener onClickAway={() => setOpenPicker(false)}>
        <FormControl variant="outlined" fullWidth sx={{ my: { xs: 1, md: 2 } }}>
          <InputLabel shrink htmlFor="date-picker" sx={{ ...inputLabelStyles }}>
            {RemoveAsterikFromstring(label)}
            {checkAsterikInString(label) && <span style={{ color: 'red' }}>*</span>}
          </InputLabel>
          <DesktopDatePicker
            open={openPicker}
            onOpen={() => {
              if (!iconClicked.current) {
                setOpenPicker(true);
              }
            }}
            onClose={() => {
              if (!iconClicked.current) {
                setOpenPicker(false);
              }
              iconClicked.current = false;
              setTouched(true);
            }}
            shouldDisableDate={(date) => {
              if (availableDates.length) {
                const formattedDate = moment(date).format('YYYY-MM-DD');
                return !availableDates.includes(formattedDate);
              }
              if (minDate && isDateSame(minDate, date)) {
                return false;
              } else if (maxDate && moment(maxDate).isBefore(moment(date))) {
                return true;
              } else if (minDate && moment(minDate).isAfter(moment(date))) {
                return true;
              }
              return false;
            }}
            shouldDisableYear={(date) => {
              if (
                maxDate &&
                moment(new Date(maxDate).setFullYear(new Date(maxDate).getFullYear() + 1)).isBefore(
                  moment(date)
                )
              ) {
                return true;
              } else if (minDate && isDateSame(minDate, date)) {
                return false;
              } else if (minDate && moment(minDate).isAfter(moment(date))) {
                return true;
              }
              return false;
            }}
            value={value || field.value}
            inputFormat="DD/MM/yyyy"
            onChange={onChange}
            onViewChange={handleViewChange}
            disabled={disabled}
            views={['year', 'month', 'day']}
            InputAdornmentProps={{ position: 'start' }}
            renderInput={(params) => (
              <TextField
                {...params}
                onBlur={(e) => {
                  params.onBlur?.(e);
                  setTouched(true);
                }}
                fullWidth
                id="date-picker"
                placeholder={placeholder}
                sx={{
                  ...textFieldStyles,
                  display: 'flex',
                  flexDirection: 'row-reverse',
                  '& .Mui-disabled': {
                    cursor: !disabled ? 'text' : 'not-allowed !important',
                    pointerEvents: 'all',
                  },
                }}
                disabled={disabled}
                autoComplete="off"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: null,
                  startAdornment: (
                    <InputAdornment
                      aria-label="Open calendar"
                      title="Open calendar"
                      position="start">
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          transition: 'background-color 0.3s',
                          '&:hover': {
                            backgroundColor: disabled ? 'transparent' : '#f0f0f0',
                          },
                        }}
                        onClick={handleIconClick}>
                        <CardMedia
                          component="img"
                          src="/images/calender.svg"
                          alt="Calender Image"
                          sx={{ width: 17, height: 17 }}
                        />
                      </Box>
                    </InputAdornment>
                  ),
                }}
                onClick={(e) => {
                  // Close the picker when clicking on TextField
                  e.stopPropagation();
                  setOpenPicker(false);
                  iconClicked.current = false;
                }}
              />
            )}
            disableCloseOnSelect={false}
            showToolbar={false}
            disableHighlightToday={disableHighlightToday}
          />
          {errorText && (
            <FormHelperText error sx={{ marginLeft: 'unset' }}>
              {errorText}
            </FormHelperText>
          )}
        </FormControl>
      </ClickAwayListener>
    </LocalizationProvider>
  );
};
